/**
 * Select Component
 *
 * Native select element with consistent styling matching the design system.
 */

import { forwardRef, type SelectHTMLAttributes } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

import { cn } from '../../utils/cn';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  /** Array of options to display */
  options?: SelectOption[];
  /** Placeholder text shown when no value selected */
  placeholder?: string;
  /** Whether the select has an error state */
  error?: boolean;
  /** Label for the select */
  label?: string;
  /** Callback when value changes (alternative to onChange) */
  onValueChange?: (value: string) => void;
  /** Children elements (option elements, alternative to options) */
  children?: React.ReactNode;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ options = [], placeholder, error, className, label, onValueChange, onChange, children, ...props }, ref) => {
    const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      onChange?.(e);
      onValueChange?.(e.target.value);
    };

    return (
      <div className="relative">
        {label && (
          <label className="mb-1 block text-sm font-medium text-mv-gray-700">{label}</label>
        )}
        <select
          ref={ref}
          onChange={handleChange}
          className={cn(
            'flex h-10 w-full appearance-none rounded border bg-white px-3 py-2 pr-10 text-sm',
            'placeholder:text-mv-gray-400',
            'focus:border-mv-blue-500 focus:outline-none focus:ring-2 focus:ring-mv-blue-500/20',
            'disabled:cursor-not-allowed disabled:bg-mv-gray-50 disabled:opacity-50',
            error
              ? 'border-mv-red-600 focus:border-mv-red-600 focus:ring-mv-red-600/20'
              : 'border-mv-gray-300',
            className
          )}
          {...props}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {children}
          {options.map((option) => (
            <option key={option.value} value={option.value} disabled={option.disabled}>
              {option.label}
            </option>
          ))}
        </select>
        <ChevronDownIcon
          className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-mv-gray-500"
          aria-hidden="true"
        />
      </div>
    );
  }
);

Select.displayName = 'Select';
